import { Component, OnInit, HostListener, Input, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() location: string = '';
  isScrolled = false;
  isMobileMenuOpen = false;
  private routerSubscription!: Subscription;
  private closeTimeouts: { [key: string]: any } = {};
  
  // Desktop dropdown states
  desktopDropdowns: { [key: string]: boolean } = {
    services: false,
    cloudSolutions: false,
    storage: false,
    resources: false,
    about: false,
    pricing: false
  };
  
  // Mobile dropdown states
  mobileDropdowns: { [key: string]: boolean } = {
    services: false,
    cloudSolutions: false,
    storage: false,
    resources: false,
    about: false,
    pricing: false
  };
  
  // Mobile submenu states
  mobileSubmenus: { [key: string]: boolean } = {
    infra: false,
    cloud: false,
    security: false,
    dev: false,
    windows: false,
    linux: false
  };

  constructor(private router: Router) { }

  ngOnInit(): void {
    // Subscribe to router events to close menus on navigation
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeAllMenus();
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    
    // Clear all timeouts
    for (const key in this.closeTimeouts) {
      if (this.closeTimeouts[key]) {
        clearTimeout(this.closeTimeouts[key]);
      }
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > 30;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const navbar = document.querySelector('nav');
    if (navbar && !navbar.contains(event.target as Node)) {
      this.closeAllMenus();
    }
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  /**
   * Toggle desktop dropdown
   */
  toggleDesktopDropdown(dropdown: string): void {
    // Close all other desktop dropdowns
    for (const key in this.desktopDropdowns) {
      if (this.desktopDropdowns.hasOwnProperty(key) && key !== dropdown) {
        this.desktopDropdowns[key] = false;
      }
    }
    
    // Toggle the selected dropdown
    this.desktopDropdowns[dropdown] = !this.desktopDropdowns[dropdown];
  }

  /**
   * Open desktop dropdown on hover
   */
  openDesktopDropdown(dropdown: string): void {
    // Clear any pending close timeout
    if (this.closeTimeouts[dropdown]) {
      clearTimeout(this.closeTimeouts[dropdown]);
      this.closeTimeouts[dropdown] = null;
    }
    
    // Close all other dropdowns
    for (const key in this.desktopDropdowns) {
      if (this.desktopDropdowns.hasOwnProperty(key) && key !== dropdown) {
        this.desktopDropdowns[key] = false;
        // Clear their timeouts too
        if (this.closeTimeouts[key]) {
          clearTimeout(this.closeTimeouts[key]);
          this.closeTimeouts[key] = null;
        }
      }
    }
    
    this.desktopDropdowns[dropdown] = true;
  }

  /**
   * Close desktop dropdown with delay
   */
  closeDesktopDropdown(dropdown: string): void {
    // Clear any existing timeout
    if (this.closeTimeouts[dropdown]) {
      clearTimeout(this.closeTimeouts[dropdown]);
    }
    
    // Set a short delay before closing to allow moving to submenus
    this.closeTimeouts[dropdown] = setTimeout(() => {
      this.desktopDropdowns[dropdown] = false;
      this.closeTimeouts[dropdown] = null;
    }, 100); // Reduced from 200ms to 100ms for faster response
  }

  /**
   * Close all menus
   */
  closeAllMenus(): void {
    this.isMobileMenuOpen = false;
    
    // Reset all desktop dropdowns
    for (const key in this.desktopDropdowns) {
      if (this.desktopDropdowns.hasOwnProperty(key)) {
        this.desktopDropdowns[key] = false;
      }
    }
    
    // Reset all mobile dropdowns and submenus when closing
    for (const key in this.mobileDropdowns) {
      if (this.mobileDropdowns.hasOwnProperty(key)) {
        this.mobileDropdowns[key] = false;
      }
    }
    
    for (const key in this.mobileSubmenus) {
      if (this.mobileSubmenus.hasOwnProperty(key)) {
        this.mobileSubmenus[key] = false;
      }
    }
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    
    // Reset all dropdowns and submenus when closing
    for (const key in this.mobileDropdowns) {
      if (this.mobileDropdowns.hasOwnProperty(key)) {
        this.mobileDropdowns[key] = false;
      }
    }
    
    for (const key in this.mobileSubmenus) {
      if (this.mobileSubmenus.hasOwnProperty(key)) {
        this.mobileSubmenus[key] = false;
      }
    }
  }
  
  /**
   * Toggle mobile dropdown
   */
  toggleMobileDropdown(dropdown: string): void {
    // Close all other dropdowns
    for (const key in this.mobileDropdowns) {
      if (this.mobileDropdowns.hasOwnProperty(key) && key !== dropdown) {
        this.mobileDropdowns[key] = false;
        // Also close submenus when closing dropdown
        this.closeSubmenusForDropdown(key);
      }
    }
    
    // Toggle the selected dropdown
    this.mobileDropdowns[dropdown] = !this.mobileDropdowns[dropdown];
    
    // If closing dropdown, also close its submenus
    if (!this.mobileDropdowns[dropdown]) {
      this.closeSubmenusForDropdown(dropdown);
    }
  }
  
  /**
   * Toggle mobile submenu
   */
  toggleMobileSubmenu(submenu: string): void {
    // Close all other submenus in the same dropdown
    const dropdownKey = this.getDropdownKeyForSubmenu(submenu);
    if (dropdownKey) {
      for (const key in this.mobileSubmenus) {
        if (this.mobileSubmenus.hasOwnProperty(key) && key !== submenu) {
          // Only close submenus that belong to the same dropdown
          const keyDropdown = this.getDropdownKeyForSubmenu(key);
          if (keyDropdown === dropdownKey) {
            this.mobileSubmenus[key] = false;
          }
        }
      }
    }
    
    // Toggle the selected submenu
    this.mobileSubmenus[submenu] = !this.mobileSubmenus[submenu];
  }
  
  /**
   * Close submenus for a specific dropdown
   */
  private closeSubmenusForDropdown(dropdown: string): void {
    switch (dropdown) {
      case 'services':
        this.mobileSubmenus.infra = false;
        this.mobileSubmenus.cloud = false;
        this.mobileSubmenus.security = false;
        this.mobileSubmenus.dev = false;
        break;
      case 'cloudSolutions':
        this.mobileSubmenus.windows = false;
        this.mobileSubmenus.linux = false;
        break;
      case 'storage':
        // No submenus for storage
        break;
      case 'resources':
        // No submenus for resources
        break;
    }
  }
  
  /**
   * Get dropdown key for a submenu
   */
  private getDropdownKeyForSubmenu(submenu: string): string | null {
    switch (submenu) {
      case 'infra':
      case 'cloud':
      case 'security':
      case 'dev':
        return 'services';
      case 'windows':
      case 'linux':
        return 'cloudSolutions';
      default:
        return null;
    }
  }
}
